import React, { useEffect, useMemo, useState } from 'react';
import Button from './Button';

function isIos() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  return /iPad|iPhone|iPod/.test(ua);
}

function isInStandaloneMode() {
  if (typeof window === 'undefined') return false;
  const standaloneMatch = window.matchMedia?.('(display-mode: standalone)')?.matches;
  // iOS Safari
  const iosStandalone = window.navigator?.standalone;
  return Boolean(standaloneMatch || iosStandalone);
}

export default function PWAInstallButton({ className }) {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(false);

  const show = useMemo(() => {
    if (installed) return false;
    if (isInStandaloneMode()) return false;

    // Always show an install option; if the browser doesn't provide a prompt
    // we fall back to instructions.
    return true;
  }, [deferredPrompt, installed]);

  useEffect(() => {
    setInstalled(isInStandaloneMode());

    const onBeforeInstallPrompt = (e) => {
      // Chrome/Edge: allow us to trigger it later
      e.preventDefault();
      setDeferredPrompt(e);
    };

    const onAppInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    window.addEventListener('appinstalled', onAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
      window.removeEventListener('appinstalled', onAppInstalled);
    };
  }, []);

  const handleClick = async () => {
    if (isIos()) {
      window.alert(
        'iPhone/iPad: open Safari → Deel (vierkant met pijl) → “Zet op beginscherm”.'
      );
      return;
    }

    if (!deferredPrompt) {
      window.alert(
        'Installeren: open het browsermenu (⋮ of …) → “Install app” / “Toevoegen aan startscherm”.\n\nTip: herlaad de pagina één keer als je de optie niet ziet.'
      );
      return;
    }

    deferredPrompt.prompt();
    try {
      await deferredPrompt.userChoice;
    } finally {
      setDeferredPrompt(null);
    }
  };

  if (!show) return null;

  return (
    <Button
      variant="ghost"
      size="sm"
      type="button"
      onClick={handleClick}
      className={className}
      title="Installeer als app (PWA)"
    >
      Installeren
    </Button>
  );
}
